<template>
    <form v-if="!loading" @submit.prevent="itemUUID ? requestUpdate() : requestCreate()">
        <div class="mx-0 mb-4 overflow-auto">
            <ul class="nav nav-tabs flex-nowrap">
                <li
                    v-for="(item, index) of entryTypeDdl"
                    :key="index"
                    class="nav-item"
                >
                    <a
                        href="javascript:void(0);"
                        class="nav-link text-nowrap"
                        :class="{ 'active text-info font-weight-bold': entryType == item.value, 'disabled': !status.save }"
                        :aria-current="entryType == item.value ? 'page' : null"
                        :aria-disabled="!status.save"
                        @click.prevent="changeEntryType(item.value)"
                    >
                        {{ item.name }}
                    </a>
                </li>
            </ul>
        </div>
        <b-form-group v-if="entryType == 'user'" :label="$t('module.user')" label-cols="3" label-align="left" class="autocomplete-group fade-in">
            <div v-if="!createForm.user" class="row">
                <div class="col pr-1">
                    <div class="position-relative">
                        <b-form-input
                            v-model="userSearchParameter"
                            type="text"
                            class="autocomplete-input"
                            :class="{ 'is-invalid': submitted && !$v.createForm.user.required }"
                            :placeholder="$t('label.search')"
                            :disabled="!status.save"
                            autocomplete="off"
                            @input="userOptions = []"
                            @keydown.enter.prevent="userEmailSearch"
                        />
                        <ul v-if="userOptions.length > 0" class="autocomplete-dropdown">
                            <li
                                v-for="(item, index) in userOptions"
                                :key="index"
                                class="autocomplete-option"
                                @click="selectUser(item)"
                            >
                                {{ item.text }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col-auto pl-1">
                    <b-button type="button" class="w-100" variant="info" :disabled="!status.save" v-b-tooltip.hover :title="$t('label.search')" @click="userEmailSearch()">
                        <i class="mdi mdi-magnify"></i>
                    </b-button>
                </div>
            </div>
            <div v-else class="d-flex justify-content-between align-items-center p-2 border rounded">
                <span class="text-break mr-2">{{ createForm.user.text }}</span>
                <b-button type="button" size="sm" variant="danger" class="flex-shrink-0" :disabled="!status.save" @click="removeUser">
                    <i class="mdi mdi-close"></i>
                </b-button>
            </div>
            <div v-if="submitted && !$v.createForm.user.required" class="invalid-feedback d-block text-right">
                {{ $t('message.requiredX', [$t('module.user')]) }}
            </div>
        </b-form-group>
        <div class="fade-in" v-else>
            <b-form-group :label="$t('label.name')" label-cols="3" label-align="left">
                <b-form-input v-model.trim="createForm.name" :class="{ 'is-invalid': submitted && !$v.createForm.name.required }" autocomplete="off" />
                <b-form-invalid-feedback v-if="submitted && !$v.createForm.name.required" class="text-right">{{ $t('message.requiredX', [$t('label.name')]) }}</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group :label="$t('label.email')" label-cols="3" label-align="left">
                <b-form-input v-model.trim="createForm.email" autocomplete="off" />
            </b-form-group>
            <b-form-group :label="$t('label.phone')" label-cols="3" label-align="left">
                <vue-tel-input
                    v-model.trim="createForm.phoneNumber"
                    :defaultCountry="createForm.phoneIso || 'MY'"
                    :autoDefaultCountry="false"
                    :autoFormat="false"
                    autocomplete="off"
                    v-on:country-changed="(country) => phoneCountryChanged(country)"
                />
            </b-form-group>
        </div>
        <hr>
        <div class="row">
            <div class="col-md-6">
                <b-form-group label="Extension" label-cols="6" label-align="left">
                    <b-form-input v-model.trim="createForm.ext" autocomplete="off" />
                </b-form-group>
            </div>
            <div class="col-md-6">
                <b-form-group label="DID call" label-cols="6" label-align="left">
                    <b-form-input v-model.trim="createForm.didCall" autocomplete="off" />
                </b-form-group>
            </div>
        </div>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button type="button" variant="danger" class="mr-2 col-3" :disabled="!status.save" @click="$emit('done')">
                {{ $t('label.cancel') }}
            </b-button>
            <b-button type="submit" variant="info" class="col-3" :disabled="!status.save || !loaded">
                <span v-if="status.save">{{ itemUUID ? $t('label.updateX', []) : $t('label.createX', []) }}</span>
                <b-spinner v-else small />
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-3">
        <b-spinner class="m-5" variant="info" role="status" />
    </div>
</template>
<script>
import { VueTelInput } from 'vue-tel-input';
import { required } from "vuelidate/lib/validators";
import { mapState } from "vuex";
import { phoneExtItemMethods, lookupMethods } from "@/state/api";
import { PHONE_EXT_ITEM_MODULE } from "@/state/api/phoneExtItem.state";

export default {
    components: { VueTelInput },
    props: {
        departmentUUID: { type: String, required: true },
        itemUUID: { type: String, default: '' },
        sequence: { type: Number, default: 1 },
    },
    validations() {
        return {
            createForm: this.entryType == 'user' ? {
                user: { required },
            } : {
                name: { required },
            },
        };
    },
    async created() {
        await this.getItemData();
    },
    data() {
        return {
            userSearchParameter: '',
            userOptions: [],
            entryType: 'user',
            entryTypeDdl: [
                { value: 'user', name: 'Existing User' },
                { value: 'manual', name: 'Enter contact details' },
            ],
            submitted: false,
            loading: false,
            loaded: !this.itemUUID,
            createForm: this.defaultForm(),
        };
    },
    methods: {
        ...phoneExtItemMethods,
        ...lookupMethods,
        async userEmailSearch() {
            const search = this.userSearchParameter.trim();
            this.userOptions = [];
            if (search && !this.createForm.user && this.status.save) {
                await this.lookupUserEmail([search])
                    .then((result) => {
                        if (result?.success && !this.createForm.user && this.entryType == 'user' && this.userSearchParameter.trim() == search) {
                            this.userOptions = this.userEmailDdl;
                        }
                    });
            }
        },
        selectUser(user) {
            this.createForm.user = user;
            this.userSearchParameter = '';
            this.userOptions = [];
        },
        removeUser() {
            this.createForm.user = null;
            this.userSearchParameter = '';
            this.userOptions = [];
        },
        changeEntryType(value) {
            if (!this.status.save) return;
            this.entryType = value;
            this.userSearchParameter = '';
            this.userOptions = [];
            this.resetValidation();
        },
        resetValidation() {
            this.submitted = false;
            this.$nextTick(() => this.$v.$reset());
        },
        defaultForm() {
            return {
                uuid: '',
                user: null,
                name: '',
                email: '',
                phoneCode: '+60',
                phoneNumber: '',
                phoneIso: 'MY',
                ext: '',
                didCall: '',
                sequence: this.sequence,
            };
        },
        async getItemData() {
            if (this.itemUUID) {
                this.loading = true;
                await this.getPhoneExtItem({uuid: this.itemUUID})
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                user: this.data.user ? {
                                    value: this.data.user.uuid,
                                    text: this.data.user.fullName || [this.data.user.firstName, this.data.user.lastName].filter(Boolean).join(' ') + ' ' + (this.data.user.email ? '(' +this.data.user.email+ ')' : ''),
                                } : null,
                                name: this.data.name,
                                email: this.data.email,
                                phoneCode: this.data.phoneCode,
                                phoneNumber: String(this.data.phoneNumber ?? ''),
                                phoneIso: this.data.phoneIso,
                                ext: this.data.ext,
                                didCall: this.data.didCall,
                                sequence: this.data.sequence,
                            };
                            this.entryType = this.data.user ? 'user' : 'manual';
                            this.loaded = true;
                        }
                    });
                this.loading = false;
            }
        },
        phoneCountryChanged(country) {
            this.createForm.phoneCode = '+' + country.dialCode;
            this.createForm.phoneIso = country.iso2;
        },
        requestCreate() {
            if (!this.status.save || !this.loaded) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) return;
            let data = {
                uuid: this.createForm.uuid,
                phoneExtDepartmentUuid: this.departmentUUID,
                userUuid: this.entryType == 'user' ? this.createForm.user.value : null,
                name: this.entryType == 'manual' ? this.createForm.name : null,
                email: this.entryType == 'manual' ? this.createForm.email : null,
                phoneCode: this.entryType == 'manual' ? this.createForm.phoneCode : null,
                phoneNumber: this.entryType == 'manual' ? this.createForm.phoneNumber : null,
                phoneIso: this.entryType == 'manual' ? this.createForm.phoneIso : null,
                ext: this.createForm.ext,
                didCall: this.createForm.didCall,
                sequence: this.createForm.sequence,
            };
            this.createPhoneExtItem({data})
                .then((result) => this.$nextTick(() => {
                    if (result?.success) {
                        this.$emit('reload');
                        this.$emit('done');
                    }
                }));
        },
        requestUpdate() {
            if (!this.status.save || !this.loaded) return;
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) return;
            let data = {
                uuid: this.createForm.uuid,
                phoneExtDepartmentUuid: this.departmentUUID,
                userUuid: this.entryType == 'user' ? this.createForm.user.value : null,
                name: this.entryType == 'manual' ? this.createForm.name : null,
                email: this.entryType == 'manual' ? this.createForm.email : null,
                phoneCode: this.entryType == 'manual' ? this.createForm.phoneCode : null,
                phoneNumber: this.entryType == 'manual' ? this.createForm.phoneNumber : null,
                phoneIso: this.entryType == 'manual' ? this.createForm.phoneIso : null,
                ext: this.createForm.ext,
                didCall: this.createForm.didCall,
                sequence: this.createForm.sequence,
            };
            this.updatePhoneExtItem({data})
                .then((result) => this.$nextTick(() => {
                    if (result?.success) {
                        this.$emit('reload');
                        this.$emit('done');
                    }
                }));
        },
    },
    computed: {
        userEmailDdl() {
            return this.$store.state.api.lookup.userEmailDdl || [];
        },
        ...mapState(PHONE_EXT_ITEM_MODULE, ['status']),
        data() {
            return this.$store.state.api.phoneExtItem.details;
        },
    },
};
</script>

<style scoped>
.autocomplete-group {
    position: relative;
    z-index: 100;
    overflow: visible !important;
}

.autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 9999 !important;

    max-height: 200px;
    overflow-y: auto;

    background: white;
    border: 1px solid #ccc;
    border-radius: 0 0 6px 6px;

    list-style: none;
    padding: 0;
    margin: 0;

    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.autocomplete-option {
    padding: 10px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.autocomplete-option:hover {
    background-color: #f5f5f5;
}
</style>